import React, { useState, useEffect } from "react";
import BemIt, { addClass } from "@gtechdoodler/bem-it";
import Icon from "../../imagery/Icon";
import Typography from "../../typography/Typography";
import throttle from "lodash/throttle";
import useViewport from "../../../hooks/useViewport";
import { FloatingButtonProps } from "./FloatingButton.types";
import {
	MOD_BACK_TO_TOP,
	MOD_FILLED,
	MOD_ICON_ONLY,
	MOD_SCROLLED,
	StyledFloatingButton,
} from "./styling/FloatingButton.styles";

export const WINDOW_INNER_HEIGHT_MULTIPLIER = 3;

const FloatingButton: React.FC<FloatingButtonProps> = ({
	className,
	text,
	onClick,
	...props
}) => {
	const vp = useViewport();
	const block = new BemIt("FloatingButton");
	const iconOnly = vp.lessThan.md || (!props.backToTop && !text);
	const iconSize = iconOnly ? "md" : "sm";
	const [scrolled, setScrolled] = useState(false);
	const floatingButtonClassNames = addClass(className).after(
		block.mod({
			[MOD_ICON_ONLY]: iconOnly,
			[MOD_FILLED]: !iconOnly,
			[MOD_BACK_TO_TOP]: props.backToTop,
			[MOD_SCROLLED]: scrolled,
		})
	);

	useEffect(() => {
		const handleScroll = () => {
			setScrolled(
				window.scrollY > window.innerHeight * WINDOW_INNER_HEIGHT_MULTIPLIER
			);
		};

		if (props.backToTop) {
			window.addEventListener("scroll", throttle(handleScroll, 200));
		}

		return () => {
			if (props.backToTop) {
				window.removeEventListener("scroll", handleScroll);
			}
		};
	}, [props.backToTop]);

	const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		if (props.backToTop) {
			window.scrollTo({ top: 0, behavior: "smooth" });
		}

		if (onClick) {
			onClick(event);
		}
	};

	return (
		<StyledFloatingButton
			className={floatingButtonClassNames}
			onClick={handleClick}
			{...props}
		>
			{vp.greaterThan.md && (
				<Typography weight="heaviest" size="body" flex="1">
					{props.backToTop ? "Back to top" : text}
				</Typography>
			)}
			{/*
        "props.icon" is a required prop, but to satisfy the TypeScript conditionals,
        we need to include a fallback in the following ternary condition. We've used
			  "arrow-right" because it's as good as any, but this condition should never
        result in "arrow-right" being used. If not a Back to top button, props.icon should
        always have a value.
      */}
			<Icon
				name={props.backToTop ? "arrow-up" : props.icon || "arrow-right"}
				size={iconSize}
				variant={iconOnly ? "icon-only" : "filled"}
			/>
		</StyledFloatingButton>
	);
};

export default FloatingButton;
